/**
 * TanStack Query hook for server health. Replaces ServerStatusContext.
 *
 * Benefit: one shared health request per key; refetchInterval handles polling without
 * manual setInterval/cleanup in a context; any component that needs health uses this hook
 * and gets the same cached result.
 */
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services';
import { unwrapServiceResultAsync } from '../services/utils/errorUtils';

const HEALTH_QUERY_KEY = ['health'] as const;
const HEALTH_REFETCH_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Server health query.
 *
 * Flow:
 * 1. On mount (or when any component first uses this hook), TanStack Query runs queryFn
 *    (healthCheck API), caches the result under HEALTH_QUERY_KEY.
 * 2. refetchInterval (5 min) triggers background refetches so server status stays current
 *    without manual setInterval/cleanup.
 * 3. All components using useHealthQuery share the same cache; one request serves everyone.
 * 4. Return value extends the raw query with convenience fields: status (API 'UP'|'DOWN'),
 *    serverUp (boolean), lastChecked (Date), and explicit isLoading. ...query spreads
 *    the rest (data, error, isFetching, refetch, etc.) for advanced use.
 */
export function useHealthQuery() {
  const query = useQuery({
    queryKey: HEALTH_QUERY_KEY,
    queryFn: () => unwrapServiceResultAsync(apiService.health.healthCheck()),
    refetchInterval: HEALTH_REFETCH_MS,
  });
  return {
    /** Spread the query object which copies all of those properties into this object. */
    ...query,
    /** Health API status ('UP' | 'DOWN'). Use this instead of query.status for server health. */
    status: query.data?.status,
    /** React Query status ('pending' | 'error' | 'success') for loading/error UI. */
    queryStatus: query.status,
    serverUp: query.data?.status === 'UP',
    lastChecked: query.data?.timestamp ? new Date(query.data.timestamp) : null,
    /** True while the initial health request is in flight (from TanStack Query via ...query). */
    isLoading: query.isLoading,
  };
}
